import * as XLSX from 'xlsx';
import { Property } from '../types/property';

export const PROPERTY_EXCEL_HEADERS = [
  'title',
  'description',
  'price',
  'type',
  'propertyType',
  'area_m2',
  'address',
  'city',
  'state',
  'country',
  'lat',
  'lng',
  'bedrooms',
  'bathrooms',
  'parking',
  'condition',
  'yearBuilt',
  'furnished',
  'features',
  'images'
];

/**
 * Parsea un número de forma segura, manejando formatos con puntos y comas
 * (estándar e hispano).
 */
export const safeParseNumber = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return value;
  
  try {
    // Limpiar espacios y convertir a string
    let strValue = String(value).trim().replace(/\s/g, '');
    
    const hasComma = strValue.includes(',');
    const hasDot = strValue.includes('.');
    
    if (hasComma && hasDot) {
      const lastCommaIndex = strValue.lastIndexOf(',');
      const lastDotIndex = strValue.lastIndexOf('.');
      
      if (lastCommaIndex > lastDotIndex) {
        // Formato hispano: 1.234,56 -> miles con punto, decimal con coma
        strValue = strValue.replace(/\./g, '').replace(',', '.');
      } else {
        // Formato estándar: 1,234.56 -> miles con coma, decimal con punto
        strValue = strValue.replace(/,/g, '');
      }
    } else if (hasComma) {
      // Solo tiene coma: 1234,56 -> la tratamos como decimal
      strValue = strValue.replace(',', '.');
    } else if (hasDot) {
      // Solo tiene punto: puede ser 1.250 (mil doscientos cincuenta) o 1.25 (uno punto veinticinco)
      // En el contexto de precios inmobiliarios, es MUCHO más probable que sea miles.
      // Si el punto está seguido de exactamente 3 dígitos, casi seguro son miles.
      const parts = strValue.split('.');
      if (parts[parts.length - 1].length === 3) {
        strValue = strValue.replace(/\./g, '');
      } else {
        // Si no son 3 dígitos, lo dejamos como decimal (ej. 1.2 o 1.25)
      }
    }
    
    const parsed = parseFloat(strValue);
    return isNaN(parsed) ? 0 : parsed;
  } catch (error) {
    return 0;
  }
};

export const geocodeAddress = async (address: string, country: string): Promise<{ lat: number, lng: number } | null> => {
  try {
    const searchQuery = `${address}, ${country}`;
    const params = new URLSearchParams({
      q: searchQuery,
      format: 'json',
      addressdetails: '1',
      limit: '1',
      'accept-language': 'es'
    });

    const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
      headers: {
        'Accept': 'application/json',
        'User-Agent': 'Propiedades-LATAM-App'
      }
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon)
      };
    }
    return null;
  } catch (error) {
    console.error('Error in geocoding:', error);
    return null;
  }
};

export const generatePropertyExcelTemplate = () => {
  const types = ['sale', 'rent', 'temporal'];
  const propertyTypes = ['casa', 'apartamento', 'departamento', 'terreno', 'local', 'oficina', 'casa_comercial', 'industrial', 'parcelacion'];
  
  const exampleData: any[] = [];
  
  // Generar todas las combinaciones posibles
  types.forEach(type => {
    propertyTypes.forEach(pType => {
      exampleData.push({
        title: `Ejemplo: ${pType.charAt(0).toUpperCase() + pType.slice(1)} en ${type === 'sale' ? 'Venta' : type === 'rent' ? 'Arriendo' : 'Temporal'}`,
        description: `Hermosa propiedad de tipo ${pType} disponible para ${type}...`,
        price: type === 'sale' ? 250000 : 1500,
        type: type,
        propertyType: pType,
        area_m2: 120,
        address: 'Av. Principal 123',
        city: 'Ciudad de Ejemplo',
        state: 'Estado de Ejemplo',
        country: 'Venezuela',
        lat: '', // Dejar vacío para probar geocodificación automática
        lng: '',
        bedrooms: 3,
        bathrooms: 2,
        parking: 1,
        condition: 'Excelente',
        yearBuilt: 2020,
        furnished: 'false',
        features: 'Seguridad 24/7, Estacionamiento',
        images: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914'
      });
    });
  });

  // Crear libro de trabajo y hoja
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(exampleData, { header: PROPERTY_EXCEL_HEADERS });
  
  XLSX.utils.book_append_sheet(wb, ws, 'Propiedades');
  
  // Generar buffer binario
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

/**
 * Busca un valor en una fila de Excel probando múltiples variantes de nombre de columna.
 */
export const getRowValue = (row: any, keys: string[]): any => {
  // Primero intentamos los nombres exactos proporcionados
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) return row[key];
  }

  // Si no se encuentra, buscamos de forma insensible a mayúsculas/minúsculas y espacios
  const rowKeys = Object.keys(row);
  for (const key of keys) {
    const normalizedTarget = key.toLowerCase().trim();
    const match = rowKeys.find(rk => rk.toLowerCase().trim() === normalizedTarget);
    if (match && row[match] !== undefined && row[match] !== null) return row[match];
  }

  return undefined;
};

export const parsePropertyExcel = (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Obtenemos los datos brutos
        const results = XLSX.utils.sheet_to_json(worksheet) as any[];
        
        // Normalizamos las cabeceras para facilitar el mapeo
        const normalizedResults = results.map(row => {
          const newRow: any = {};
          Object.keys(row).forEach(key => {
            // "Price " -> "price", "Precio" -> "precio"
            const normalizedKey = key.toLowerCase().trim();
            newRow[normalizedKey] = row[key];
            
            // Mantener también la clave original por si acaso
            if (normalizedKey !== key) {
              newRow[key] = row[key];
            }
          });
          return newRow;
        });

        if (process.env.NODE_ENV === 'development') {
          console.log('[Excel] Datos parseados y normalizados:', normalizedResults[0]);
        }

        resolve(normalizedResults);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
};

export const mapExcelRowToProperty = (row: any): Partial<Property> => {
  const price = getRowValue(row, ['price', 'precio', 'valor', 'costo']);
  const area = getRowValue(row, ['area_m2', 'area', 'metros', 'superficie']);
  const type = getRowValue(row, ['type', 'tipo_operacion', 'operacion']);
  const pType = getRowValue(row, ['propertytype', 'tipo_propiedad', 'tipo']);
  const lat = getRowValue(row, ['lat', 'latitud']);
  const lng = getRowValue(row, ['lng', 'longitud']);
  const address = getRowValue(row, ['address', 'direccion', 'ubicacion']);

  return {
    title: String(getRowValue(row, ['title', 'titulo', 'nombre']) || ''),
    description: String(getRowValue(row, ['description', 'descripcion']) || ''),
    price: safeParseNumber(price),
    type: (type as 'sale' | 'rent' | 'temporal') || 'sale',
    propertyType: (pType as any) || 'casa',
    location: {
      address: String(address || ''),
      city: String(getRowValue(row, ['city', 'ciudad']) || ''),
      state: String(getRowValue(row, ['state', 'estado', 'provincia']) || ''),
      country: String(getRowValue(row, ['country', 'pais']) || 'Venezuela'),
      coordinates: {
        lat: safeParseNumber(lat),
        lng: safeParseNumber(lng)
      }
    },
    bedrooms: safeParseNumber(getRowValue(row, ['bedrooms', 'habitaciones', 'cuartos', 'dormitorios'])),
    bathrooms: safeParseNumber(getRowValue(row, ['bathrooms', 'baños'])),
    area: safeParseNumber(area),
    parking: safeParseNumber(getRowValue(row, ['parking', 'estacionamiento', 'puestos', 'cochera'])),
    condition: String(getRowValue(row, ['condition', 'estado_propiedad']) || ''),
    yearBuilt: getRowValue(row, ['yearbuilt', 'año', 'construccion']) ? safeParseNumber(getRowValue(row, ['yearbuilt', 'año', 'construccion'])) : undefined,
    furnished: String(getRowValue(row, ['furnished', 'amoblado', 'muebles'])).toLowerCase() === 'true',
    features: getRowValue(row, ['features', 'caracteristicas']) ? String(getRowValue(row, ['features', 'caracteristicas'])).split(',').map((f: string) => f.trim()) : [],
    images: getRowValue(row, ['images', 'fotos', 'imagenes']) ? String(getRowValue(row, ['images', 'fotos', 'imagenes'])).split(',').map((i: string) => i.trim()) : [],
    status: 'active'
  };
};

export const validatePropertyRow = (row: any): string[] => {
  const errors: string[] = [];
  const title = getRowValue(row, ['title', 'titulo', 'nombre']);
  const price = getRowValue(row, ['price', 'precio', 'valor']);
  const address = getRowValue(row, ['address', 'direccion']);
  
  if (!title) errors.push('El título es requerido');
  
  const parsedPrice = safeParseNumber(price);
  
  // Log para depuración en la consola del navegador
  if (process.env.NODE_ENV === 'development') {
    console.log('[Excel] Validando fila:', {
      detectado: Object.keys(row).filter(k => row[k] !== undefined),
      valorPrecioOriginal: price,
      precioParseado: parsedPrice
    });
  }

  if (price === undefined || price === null || price === '') {
    errors.push('El precio es requerido');
  } else if (isNaN(parsedPrice) || parsedPrice <= 0) {
    errors.push(`El precio "${price}" no es un número válido mayor a 0`);
  }
  
  if (!address) errors.push('La dirección es requerida');
  
  const type = String(getRowValue(row, ['type', 'tipo_operacion']) || '').toLowerCase();
  if (!['sale', 'rent', 'temporal'].includes(type)) {
    errors.push('El tipo de operación debe ser sale, rent o temporal');
  }
  
  const pType = String(getRowValue(row, ['propertytype', 'tipo_propiedad']) || '').toLowerCase();
  if (!['casa', 'apartamento', 'departamento', 'terreno', 'local', 'oficina', 'casa_comercial', 'industrial', 'parcelacion'].includes(pType)) {
    errors.push(`Tipo de propiedad "${pType}" no válido`);
  }
  
  return errors;
};
